/**
 * Maps a team name to a hue color hex value.
 * Used for the mouse-tracking glow effect on cards.
 */

const hueColors: string[] = [
  "#E11837", // red
  "#3B82F6", // blue
  "#22C55E", // green
  "#A855F7", // purple
  "#F59E0B", // amber
  "#06B6D4", // cyan
  "#EC4899", // pink
  "#F97316", // orange
];

// Known team name → color mappings
const knownTeams: Record<string, string> = {
  engineering: "#E11837",
  eng: "#E11837",
  residential: "#3B82F6",
  golf: "#22C55E",
  commercial: "#F59E0B",
  design: "#A855F7",
  product: "#06B6D4",
  marketing: "#EC4899",
  operations: "#F97316",
};

// ── Level-based icons (20×20 viewBox, Fluent-style) ────────────────────

// Product Line — unique per name (kept from before)
const knownProductLineIcons: Record<string, string> = {
  engineering:
    // Wrench/gear
    "M14.7 6.3a1 1 0 000 1.4l-1.6 1.6a4 4 0 01-5.4 5.4L6.1 16.3a1 1 0 01-1.4 0l-1-1a1 1 0 010-1.4l1.6-1.6a4 4 0 015.4-5.4l1.6-1.6a1 1 0 011.4 0l1 1zM10 8.5a2 2 0 100 4 2 2 0 000-4z",
  golf:
    // Flag
    "M6 2v16M6 2l8 4-8 4M3 17a1 1 0 011-1h4a1 1 0 011 1v0a1 1 0 01-1 1H4a1 1 0 01-1-1v0z",
  residential:
    // House
    "M10 2.5l-7.5 6v9a1 1 0 001 1h4.5v-5a1 1 0 011-1h2a1 1 0 011 1v5h4.5a1 1 0 001-1v-9l-7.5-6z",
  commercial:
    // Building
    "M4 3a1 1 0 011-1h10a1 1 0 011 1v15H4V3zm3 2h2v2H7V5zm4 0h2v2h-2V5zM7 9h2v2H7V9zm4 0h2v2h-2V9zM8 14h4v4H8v-4z",
};

// Product — box/package (single icon for all products)
const PRODUCT_ICON =
  "M10 2L3 6v8l7 4 7-4V6l-7-4zM3 6l7 4m0 0v8m0-8l7-4";

// Discipline — users/people (single icon for all disciplines)
const DISCIPLINE_ICON =
  "M12 4.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM4 16.5c0-2.5 2-4.5 5.5-4.5s5.5 2 5.5 4.5M15.5 6a2 2 0 110 4M17.5 16c0-1.5-1-3-2.5-3.5";

// Group — unique per name
const knownGroupIcons: Record<string, string> = {
  precisionirrigation:
    // Droplet
    "M10 2C10 2 4 9 4 13a6 6 0 0012 0c0-4-6-11-6-11z",
  "l&c":
    // Building/landscape
    "M3 17h14M5 17V9l4-4 4 4v8m-8-5h2v2H5v-2zm4 0h2v2H9v-2zm6-4v9m0-9l3-2v11",
};

// Default fallback — generic folder
const DEFAULT_ICON =
  "M3 7a2 2 0 012-2h3.28a2 2 0 011.45.63L11 7h4a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2V7z";

function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

export function getTeamHueColor(teamName: string | undefined | null): string {
  if (!teamName) return "#3B82F6";
  
  const lower = teamName.toLowerCase();
  
  const known = knownTeams[lower];
  if (known) return known;
  
  const index = simpleHash(lower) % hueColors.length;
  return hueColors[index] ?? "#3B82F6";
}

/** Returns an SVG path data string for the team icon based on level and name */
export function getTeamIconPath(teamName: string | undefined | null, level?: string | null): string {
  if (!teamName) return DEFAULT_ICON;
  const lower = teamName.toLowerCase();

  switch (level) {
    case "group": {
      return knownGroupIcons[lower] ?? DEFAULT_ICON;
    }
    case "product-line": {
      // Product lines get unique icons per name
      return knownProductLineIcons[lower] ?? DEFAULT_ICON;
    }
    case "product": {
      // All products share the same icon
      return PRODUCT_ICON;
    }
    case "discipline": {
      // All disciplines share the same icon
      return DISCIPLINE_ICON;
    }
    default:
      return DEFAULT_ICON;
  }
}
